import { useAppDispatch } from "hooks/redux";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { websiteBuilderUpdateSelectedNode } from "redux/actions/websiteBuilder/websiteBuilder";
import { IWebsiteBuilderClipboard } from "pages/secure/facility/websiteBuilder/editor/hooks/clipboard/useWebsiteBuilderClipboard";
import { TWebsiteBuilderTreeRefMap } from "pages/secure/facility/websiteBuilder/editor/hooks/treeRefMap/useTreeRefMapRegistration";
import {
  IWebsiteBuilderContextMenuPosition,
  IWebsiteBuilderContextMenuOption,
  WebsiteBuilderContextMenu,
} from "../../helpers/WebsiteBuilderContextMenu";
import {
  TWebsiteBuilderSection,
  TWebsiteBuilderSectionType,
} from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderSection";
import { IWebsiteBuilderSectionOperations } from "pages/secure/facility/websiteBuilder/editor/hooks/sectionOperations/useWebsiteBuilderSectionOperations";
import { IWebsiteBuilderRoot } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderRoot";
import { TWebsiteBuilderScrollContainerRef } from "pages/secure/facility/websiteBuilder/editor/WebsiteBuilderEditor";

export interface IUseSectionViewContextMenuArgs {
  node: TWebsiteBuilderSection;
  parentNode: IWebsiteBuilderRoot;
  type: TWebsiteBuilderSectionType;
  sectionOperations: IWebsiteBuilderSectionOperations;
  clipboard: IWebsiteBuilderClipboard;
  selected: boolean;
  menuActive: boolean;
  leftX: number;
  treeRefMap: TWebsiteBuilderTreeRefMap;
  scrollContainerRef: TWebsiteBuilderScrollContainerRef;
}

interface ISectionViewContextMenuState {
  position: IWebsiteBuilderContextMenuPosition;
}

const initialState: ISectionViewContextMenuState = {
  position: null,
};

interface IUseSectionViewContextMenu {
  viewContextMenu: JSX.Element;
  handleViewOpenContextMenu: (position: IWebsiteBuilderContextMenuPosition) => void;
}

export function useSectionViewContextMenu(args: IUseSectionViewContextMenuArgs): IUseSectionViewContextMenu {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [state, setState] = useState<ISectionViewContextMenuState>(cloneDeep(initialState));

  const modifierKey = window.navigator.userAgent.includes("Mac") ? "⌘" : "Ctrl";

  const headerSection = args.type === "header";
  const footerSection = args.type === "footer";

  const outerSection = headerSection || footerSection;

  useEffect(() => {
    if (!args.menuActive) {
      setState(cloneDeep(initialState));
    }
  }, [args.menuActive]);

  const { canMoveUp, canMoveDown } = useMemo(() => {
    if (outerSection) {
      return { canMoveUp: false, canMoveDown: false };
    }

    const index = args.parentNode.children.findIndex(child => child.id === args.node.id);

    let canMoveUp = true;
    let canMoveDown = true;

    if (index === 0) {
      canMoveUp = false;
    }

    if (index === args.parentNode.children.length - 1) {
      canMoveDown = false;
    }

    return { canMoveUp, canMoveDown };
  }, [outerSection, args.parentNode.children]);

  const moveSectionOptions: IWebsiteBuilderContextMenuOption[] = [
    {
      label: t("Move Section Up"),
      shortcut: "",
      onClick: () =>
        args.sectionOperations.moveSectionUp(args.node, args.parentNode, args.treeRefMap, args.scrollContainerRef),
      disabled: !canMoveUp,
    },
    {
      label: t("Move Section Down"),
      shortcut: "",
      onClick: () =>
        args.sectionOperations.moveSectionDown(args.node, args.parentNode, args.treeRefMap, args.scrollContainerRef),
      disabled: !canMoveDown,
    },
  ];

  const creationMenuOptions: IWebsiteBuilderContextMenuOption[] = [
    {
      label: t("Copy"),
      shortcut: `${modifierKey} + C`,
      onClick: () => args.sectionOperations.copySection(args.node),
      disabled: outerSection,
    },
    {
      label: t("Paste"),
      shortcut: `${modifierKey} + V`,
      onClick: () =>
        args.sectionOperations.pasteAtSection(
          args.node,
          args.parentNode,
          args.leftX,
          args.treeRefMap,
          args.scrollContainerRef,
        ),
      disabled: args.clipboard.empty,
    },
    {
      label: t("Duplicate"),
      shortcut: `${modifierKey} + D`,
      onClick: () => args.sectionOperations.duplicateSection(args.node, args.parentNode, args.treeRefMap),
      disabled: outerSection,
    },
  ];

  const deletionMenuOptions: IWebsiteBuilderContextMenuOption[] = [
    {
      label: t("Delete"),
      shortcut: "Del",
      onClick: () => args.sectionOperations.deleteSection(args.node, args.parentNode),
      disabled: outerSection,
    },
    {
      label: t("Cut"),
      shortcut: `${modifierKey} + X`,
      onClick: () => args.sectionOperations.cutSection(args.node, args.parentNode),
      disabled: outerSection,
    },
  ];

  function closeMenu() {
    dispatch(
      websiteBuilderUpdateSelectedNode({
        selectedNodeUpdates: {
          editingMode: "none",
        },
      }),
    );
  }

  function handleViewOpenContextMenu(position: IWebsiteBuilderContextMenuPosition) {
    if (!args.selected) {
      return;
    }

    if (!args.menuActive) {
      dispatch(
        websiteBuilderUpdateSelectedNode({
          selectedNodeUpdates: {
            editingMode: "section-context-menu",
          },
        }),
      );
    }

    setState(prev => ({
      ...prev,
      position,
    }));
  }

  function renderContextMenu() {
    if (!args.menuActive || state.position == null) {
      return null;
    } else {
      return (
        <WebsiteBuilderContextMenu
          showPopup={false}
          popupContent={null}
          position={state.position}
          sections={[moveSectionOptions, creationMenuOptions, deletionMenuOptions]}
          treeRefMap={args.treeRefMap}
          closeMenu={closeMenu}
        />
      );
    }
  }

  return {
    viewContextMenu: renderContextMenu(),
    handleViewOpenContextMenu,
  };
}
